/**
 * Lenses Tab — Shared constants
 *
 * Colors aligned with the General tab's bar charts and sentiment topics.
 * Uses the same palette as insightConstants.js and SentimentTopics.
 */
import { colors2 } from "oolib";
import {
  ArrowUpRight,
  ArrowDownRight,
  AsteriskSimple,
  Minus,
} from "@phosphor-icons/react";

// ── Signal / Sentiment colors ─────────────────────────────────
// Matches SentimentTopicsWidget: positive = green, negative = pink/red

export const SIGNAL_COLORS = {
  positive: "#3BA974",      // same as insightConstants.scoreHigh
  negative: "#E92519",      // same as insightConstants.scoreLow
  neutral: colors2.grey50,
};

// Softer fills for backgrounds (matches SentimentTopicsWidget bar colors)
export const SIGNAL_BG = {
  positive: "#CBEDD6",      // same as Top Positive Topics bar color
  negative: "#F5DCE6",      // same as Top Negative Topics bar color
  neutral: colors2.grey10,
};

// ── Status themes ─────────────────────────────────────────────
// Single source of truth for trend / health / sentiment styling.
// Each entry: { bg, color, label } plus, for trend, { accent, glow, Icon }
// for the sideboard's themed header.

export const TREND_THEME = {
  rising:    { bg: "#FF7F7F",    color: colors2.grey80, accent: "#E84A3F",      glow: "#FF6A6A4D", Icon: ArrowUpRight,   label: "RISING" },
  declining: { bg: "#6BDC58",    color: colors2.grey80, accent: "#2DA15F",      glow: "#6BDC584D", Icon: ArrowDownRight, label: "DECLINING" },
  new:       { bg: "#B19DFF",    color: colors2.grey80, accent: "#7B5BD8",      glow: "#B19DFF4D", Icon: AsteriskSimple, label: "NEW" },
  stable:    { bg: "#03143033",  color: colors2.grey70, accent: colors2.grey60, glow: "#00000014", Icon: Minus,          label: "STABLE" },
};

// Neutral trend theme for Topics — shows direction without good/bad polarity coloring
export const TOPIC_TREND_THEME = {
  rising:    { bg: "#03143033", color: colors2.grey70, accent: colors2.grey60, glow: "#00000014", Icon: ArrowUpRight,   label: "RISING" },
  declining: { bg: "#03143033", color: colors2.grey70, accent: colors2.grey60, glow: "#00000014", Icon: ArrowDownRight, label: "DECLINING" },
  new:       { bg: "#03143033", color: colors2.grey70, accent: colors2.grey60, glow: "#00000014", Icon: AsteriskSimple, label: "NEW" },
  stable:    { bg: "#03143033", color: colors2.grey70, accent: colors2.grey60, glow: "#00000014", Icon: Minus,          label: "STABLE" },
};

export const HEALTH_THEME = {
  very_negative: { bg: "#FF7F7F",      color: colors2.grey80, accent: "#E84A3F",      glow: "#FF6A6A4D", label: "VERY NEGATIVE" },
  negative:      { bg: "#FF7F7F",      color: colors2.grey80, accent: "#E84A3F",      glow: "#FF6A6A4D", label: "NEGATIVE" },
  neutral:       { bg: "#03143033",    color: colors2.grey70, accent: colors2.grey60, glow: "#00000014", label: "NEUTRAL" },
  positive:      { bg: "#6BDC58",      color: colors2.grey80, accent: "#2DA15F",      glow: "#6BDC584D", label: "POSITIVE" },
  very_positive: { bg: "#6BDC58",      color: colors2.grey80, accent: "#2DA15F",      glow: "#6BDC584D", label: "VERY POSITIVE" },
  no_data:       { bg: colors2.grey10, color: colors2.grey40, accent: colors2.grey60, glow: "#00000014", label: "NO DATA" },
};

export const SENTIMENT_THEME = {
  positive: { bg: "#6BDC58",   color: colors2.grey80, accent: "#2DA15F",      glow: "#6BDC584D", label: "POSITIVE" },
  negative: { bg: "#FF7F7F",   color: colors2.grey80, accent: "#E84A3F",      glow: "#FF6A6A4D", label: "NEGATIVE" },
  neutral:  { bg: "#03143033", color: colors2.grey70, accent: colors2.grey60, glow: "#00000014", label: "NEUTRAL" },
};

const STATUS_THEMES = { trend: TREND_THEME, health: HEALTH_THEME, sentiment: SENTIMENT_THEME };
const STATUS_FALLBACKS = { trend: "stable", health: "neutral", sentiment: "neutral" };

export function getStatusTheme(kind, key) {
  const map = STATUS_THEMES[kind];
  if (!map) return null;
  return map[key] || map[STATUS_FALLBACKS[kind]];
}

// ── Confidence ────────────────────────────────────────────────
export const CONFIDENCE_DOTS = {
  1: "\u25CB\u25CB\u25CB\u25CB\u25CB",
  2: "\u25CF\u25CB\u25CB\u25CB\u25CB",
  3: "\u25CF\u25CF\u25CF\u25CB\u25CB",
  4: "\u25CF\u25CF\u25CF\u25CF\u25CB",
  5: "\u25CF\u25CF\u25CF\u25CF\u25CF",
};

export const WARNING_SEVERITY_COLORS = {
  high: "#E92519",
  medium: "#F8BC3B",
  low: colors2.grey40,
};

// ── Overview icon map (matches patternId from cardPatterns.json) ──
export const OVERVIEW_ICON_MAP = {
  user_sentiment: "Smiley",
  user_trust: "Handshake",
  churn_risk: "Warning",
  // COMMENTED OUT — removed from overview
  // activation: "UserPlus",
  // churn_indication: "Warning",
  // delivery_health: "Timer",
  // delivery_performance: "Timer",
  // order_volume: "ShoppingCart",
  // support_volume: "Headset",
  // Legacy IDs (backward compat during migration)
  satisfaction: "Smiley",
  churn: "Warning",
};

// ── Widget layout ─────────────────────────────────────────────
export const WIDGET_HEIGHT = "56rem";
export const WIDGET_STYLE = { height: WIDGET_HEIGHT, gap: 0 };
export const WIDGET_BODY_STYLE = { overflowY: "auto", overflowX: "hidden" };

// ── Fallback period options ───────────────────────────────────
export const FALLBACK_PERIOD_OPTIONS = [
  { value: "this_year", display: "this Year" },
  { value: "this_month", display: "this Month" },
];
